/**
 * VARIATION_FORM_SECTION.JSX - Variation Basic Fields Component
 * 
 * SUMMARY:
 * Presentational component for the main variation form fields.
 * Displays name, date, profit margin, file uploads, and section selector.
 * 
 * PROPS:
 * @param {Object} fields - Form fields state
 * @param {boolean} editMode - Whether in edit mode
 * @param {Object} taskState - Task state (lock icon, etc.)
 * @param {Function} inputChangeHandler - Input change handler
 * @param {Function} profitMarginDialogHandler - Profit margin dialog handler
 * @param {Function} getFiles - Get uploaded files list
 * @param {Object} task - Task data
 * @param {string|number} templateQty - Template quantity
 * @param {Function} enableEditBtn - Check if edit is enabled
 * @param {Function} refreshHandler - Template refresh handler
 * @param {Function} renderMultiSelectJSX - Section selector renderer
 * 
 * @returns {JSX.Element} Form section
 */

import React, { Fragment } from 'react';
import CustomInput, { LockInput } from "../../../../components/inputs/new-input";
import DatePickerInput from "../../../../components/inputs/date-picker";
import FileUploader from "../../../../components/file-uploader";
import TaskTemplate from "../../add-task/taskTemplate";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import idx from 'idx';

export default function VariationFormSection({
    fields,
    editMode,
    taskState,
    inputChangeHandler,
    excludeFromFieldOps,
    setExcludeFromFieldOps,
    isFieldOpsInheritedExcluded,
    profitMarginDialogHandler,
    getFiles,
    task,
    templateQty,
    enableEditBtn,
    refreshHandler,
    renderMultiSelectJSX
}) {
    return (
        <div className="row">
            {/* Variation Status */}
            {idx(task, _ => _.task.id) && (
                <div className="col-lg-4 col-md-6 col-sm-12 mt-3">
                    <div className="form-group">
                        <div className="input-block">
                            <label className="input-label">Status</label>
                            <div style={{ 
                                padding: '8px 12px',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa'
                            }}>
                                {(() => {
                                    const status = task.task.variationStatus || 
                                        (task.task.proposal_dealt === false ? 'DRAFT' : 
                                         task.task.proposal_status || 'DRAFT');
                                    const statusConfig = {
                                        'DRAFT': { color: '#6c757d', bg: '#f8f9fa' },
                                        'SUBMITTED': { color: '#0066cc', bg: '#e6f3ff' },
                                        'PENDING': { color: '#ff8c00', bg: '#fff4e6' },
                                        'APPROVED': { color: '#28a745', bg: '#d4edda' },
                                        'REJECTED': { color: '#dc3545', bg: '#f8d7da' }
                                    };
                                    const config = statusConfig[status] || statusConfig['DRAFT'];
                                    return (
                                        <span 
                                            style={{
                                                backgroundColor: config.bg,
                                                color: config.color,
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                border: `1px solid ${config.color}20`
                                            }}
                                        >
                                            {status}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Variation Name */}
            <div className="col-lg-4 col-md-6 col-sm-12 mt-3">
                <CustomInput
                    placeholder="Enter variation name"
                    label="Variation Name"
                    {...fields.taskName}
                    className='variation-description'
                    disabled={!editMode}
                    onChange={inputChangeHandler}
                    data-cy="variation-name-input"
                />
            </div>

            {/* Date */}
            <div className="col-lg-4 col-md-6 col-sm-12 mt-3">
                <DatePickerInput
                    disabled={!editMode}
                    {...fields.date}
                    label={'Date'}
                    data-cy="variation-date"
                    onChange={(e) => inputChangeHandler({
                        target: { value: e, name: fields.date.name }
                    })}
                />
            </div>

            {/* Profit Margin */}
            <div className="col-lg-4 col-md-6 col-sm-12 mt-3">
                <LockInput
                    lock={
                        <span className="lock-icon">
                            {taskState.showlockIcon ?
                                <LockIcon onClick={() => { if (editMode) profitMarginDialogHandler() }} />
                                : <LockOpenIcon onClick={() => { if (editMode) profitMarginDialogHandler() }} />
                            }
                        </span>
                    }
                    placeholder="Enter profit margin"
                    {...fields.profitMargin}
                    label={'Profit Margin %'}
                    data-cy="profit-margin"
                    disabled={!editMode || taskState.showlockIcon}
                    onChange={(e) => {
                        if (taskState.showlockIcon) return;
                        if (e.target.value < 0) return;
                        if (fields.profitMarginType.value === "PERCENTAGE" && e.target.value > 100) return;
                        inputChangeHandler(e);
                    }}
                />
            </div>

            {/* File Upload */}
            <div className="col-lg-4 col-md-6 col-sm-12 mt-3 fileupload-section">
                <div className="file-section-part position-relative h-auto px-0">
                    <div className="file-part">
                        <FileUploader
                            fileHandler={(files) => {
                                if (!editMode) return;
                                inputChangeHandler({
                                    target: {
                                        files,
                                        type: "file",
                                        name: "attachments",
                                    },
                                });
                            }}
                        />
                        <label htmlFor="#file-section"></label>
                    </div>
                    <input type="file" id="file-section" data-cy="file-upload"></input>
                </div>
            </div>

            {/* Uploaded Documents */}
            <div className="col-lg-4 col-md-6 col-sm-12 mt-3">
                <div className="form-group">
                    <div className="input-block">
                        <label className="input-label">Uploaded Documents</label>
                        <p className="m-0 doc-info">{getFiles()}</p>
                    </div>
                </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12 mt-3 d-flex align-items-center">
                <div className="form-check m-0">
                    <input
                        id="exclude-from-field-ops"
                        type="checkbox"
                        className="form-check-input"
                        checked={isFieldOpsInheritedExcluded || excludeFromFieldOps}
                        disabled={!editMode || isFieldOpsInheritedExcluded}
                        onChange={(e) => setExcludeFromFieldOps(e.target.checked)}
                        data-cy="exclude-from-field-ops-checkbox"
                    />
                    <label
                        className="form-check-label"
                        htmlFor="exclude-from-field-ops"
                    >
                        Exclude from field ops
                    </label>
                    {isFieldOpsInheritedExcluded && (
                        <small className="text-muted d-block mt-1">
                            Inherited from parent section
                        </small>
                    )}
                </div>
            </div>

            {/* Template Selector (if has template) */}
            {idx(task, _ => _.task.templateId) && idx(task, _ => _.task.templateQuantity) > 0 && (
                <>
                    <div className="col-lg-4 col-md-6 col-sm-12 mt-3 task-selector align-self-center pt-0">
                        <TaskTemplate
                            templateId={idx(task, _ => _.task.templateId) || ''}
                            templateName={idx(task, _ => _.task.templateName) || ''}
                            selectedQty={idx(task, _ => _.task.templateQuantity) || templateQty}
                            item={task}
                            editMode={editMode}
                            enableEditBtn={enableEditBtn}
                            refreshHandler={refreshHandler}
                            isTaskTemplateQty={true}
                        />
                    </div>
                    <div className="col-lg-8 col-md-6 col-sm-12 mt-3"></div>
                </>
            )}

            {/* Section Selector (passed as prop) */}
            {renderMultiSelectJSX && renderMultiSelectJSX()}
        </div>
    );
}
